import React, { useEffect, useState } from "react";
import { auth, db, storage } from "./firebase";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useNavigate } from "react-router-dom";
import {
  Activity,
  Upload,
  FileText,
  Shield,
  CheckCircle,
  Clock,
  AlertCircle,
  X,
  Eye,
} from "lucide-react";

const VerifyDoctor = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [files, setFiles] = useState({ license: null });
  const [previews, setPreviews] = useState({ license: null });

  const navigate = useNavigate();

  // Modern styles
  const styles = {
    container: {
      minHeight: "100vh",
      background:
        "linear-gradient(135deg, #f0f9ff 0%, #ffffff 30%, #f0fdf4 100%)",
      fontFamily:
        "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    },
    header: {
      backgroundColor: "rgba(255, 255, 255, 0.8)",
      backdropFilter: "blur(20px)",
      borderBottom: "1px solid rgba(229, 231, 235, 0.5)",
      padding: "24px 0",
      marginBottom: "32px",
    },
    headerContent: {
      maxWidth: "800px",
      margin: "0 auto",
      padding: "0 24px",
      display: "flex",
      alignItems: "center",
      gap: "16px",
    },
    logoContainer: {
      background: "linear-gradient(135deg, #3b82f6, #6366f1)",
      padding: "12px",
      borderRadius: "16px",
      boxShadow: "0 10px 25px -5px rgba(59, 130, 246, 0.3)",
    },
    headerTitle: {
      fontSize: "24px",
      fontWeight: "700",
      background: "linear-gradient(135deg, #1f2937, #6b7280)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      margin: 0,
    },
    headerSubtitle: {
      fontSize: "16px",
      color: "#6b7280",
      margin: 0,
    },
    mainContent: {
      maxWidth: "800px",
      margin: "0 auto",
      padding: "0 24px 48px 24px",
    },
    card: {
      backgroundColor: "rgba(255, 255, 255, 0.9)",
      backdropFilter: "blur(20px)",
      borderRadius: "24px",
      boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.15)",
      border: "1px solid rgba(255, 255, 255, 0.2)",
      overflow: "hidden",
    },
    statusBanner: {
      padding: "24px 48px",
      textAlign: "center",
      borderBottom: "1px solid rgba(229, 231, 235, 0.3)",
    },
    statusPending: {
      background: "linear-gradient(135deg, #fef3c7, #fde68a)",
      color: "#92400e",
    },
    statusApproved: {
      background: "linear-gradient(135deg, #dcfce7, #bbf7d0)",
      color: "#166534",
    },
    statusRejected: {
      background: "linear-gradient(135deg, #fee2e2, #fecaca)",
      color: "#dc2626",
    },
    statusIcon: {
      width: "48px",
      height: "48px",
      margin: "0 auto 16px auto",
    },
    statusTitle: {
      fontSize: "20px",
      fontWeight: "600",
      marginBottom: "8px",
    },
    statusDescription: {
      fontSize: "16px",
      opacity: 0.8,
    },
    formSection: {
      padding: "48px",
    },
    sectionTitle: {
      fontSize: "20px",
      fontWeight: "600",
      color: "#111827",
      marginBottom: "16px",
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    sectionDescription: {
      fontSize: "16px",
      color: "#6b7280",
      marginBottom: "32px",
      lineHeight: "1.6",
    },
    documentsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
      gap: "24px",
      marginBottom: "32px",
    },
    documentCard: {
      border: "2px dashed #e5e7eb",
      borderRadius: "16px",
      padding: "24px",
      textAlign: "center",
      transition: "all 0.2s ease",
      backgroundColor: "#fafafa",
      cursor: "pointer",
      position: "relative",
    },
    documentCardUploaded: {
      borderColor: "#10b981",
      backgroundColor: "#dcfce7",
      borderStyle: "solid",
    },
    documentIcon: {
      width: "48px",
      height: "48px",
      margin: "0 auto 16px auto",
      padding: "12px",
      borderRadius: "12px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "#dcfce7",
    },
    documentTitle: {
      fontSize: "16px",
      fontWeight: "600",
      color: "#111827",
      marginBottom: "8px",
    },
    documentDescription: {
      fontSize: "14px",
      color: "#6b7280",
      marginBottom: "16px",
    },
    fileInput: { display: "none" },
    uploadButton: {
      padding: "12px 24px",
      background: "linear-gradient(135deg, #3b82f6, #6366f1)",
      color: "white",
      border: "none",
      borderRadius: "8px",
      fontSize: "14px",
      fontWeight: "500",
      cursor: "pointer",
      transition: "all 0.2s ease",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      margin: "0 auto",
    },
    filePreview: {
      position: "absolute",
      top: "16px",
      right: "16px",
      display: "flex",
      gap: "8px",
    },
    previewButton: {
      width: "32px",
      height: "32px",
      borderRadius: "8px",
      border: "none",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "all 0.2s ease",
    },
    viewButton: { backgroundColor: "#3b82f6", color: "white" },
    removeButton: { backgroundColor: "#ef4444", color: "white" },
    fileName: {
      fontSize: "14px",
      fontWeight: "500",
      color: "#111827",
      marginTop: "8px",
      padding: "8px 12px",
      backgroundColor: "rgba(255, 255, 255, 0.8)",
      borderRadius: "6px",
    },
    alert: {
      padding: "16px",
      borderRadius: "12px",
      fontSize: "14px",
      fontWeight: "500",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      marginBottom: "24px",
    },
    errorAlert: {
      backgroundColor: "#fee2e2",
      border: "1px solid #fecaca",
      color: "#dc2626",
    },
    successAlert: {
      backgroundColor: "#dcfce7",
      border: "1px solid #bbf7d0",
      color: "#16a34a",
    },
    submitButton: {
      width: "100%",
      padding: "16px",
      background: "linear-gradient(135deg, #3b82f6, #6366f1)",
      color: "white",
      border: "none",
      borderRadius: "12px",
      fontSize: "16px",
      fontWeight: "600",
      cursor: "pointer",
      transition: "all 0.2s ease",
      boxShadow: "0 10px 25px -5px rgba(59, 130, 246, 0.3)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
    },
    submitButtonDisabled: { opacity: 0.5, cursor: "not-allowed" },
    spinner: {
      width: "20px",
      height: "20px",
      border: "2px solid transparent",
      borderTop: "2px solid white",
      borderRadius: "50%",
      animation: "spin 1s linear infinite",
    },
    requirements: {
      backgroundColor: "#f8fafc",
      border: "1px solid #e2e8f0",
      borderRadius: "12px",
      padding: "20px",
      marginBottom: "32px",
    },
    requirementsList: { listStyle: "none", padding: 0, margin: 0 },
    requirementItem: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      padding: "8px 0",
      fontSize: "14px",
      color: "#374151",
    },
    requirementIcon: { width: "16px", height: "16px", color: "#10b981" },
  };

  const documentType = {
    key: "license",
    title: "Medical License",
    description: "Upload a clear image or PDF of your current medical license",
    iconColor: "#10b981",
  };

  useEffect(() => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      navigate("/login");
      return;
    }
    setUser(currentUser);
    checkVerification(currentUser.uid);
  }, [navigate]);

  const checkVerification = async (uid) => {
    try {
      const docSnap = await getDoc(doc(db, "verifications", uid));
      if (docSnap.exists()) {
        const data = docSnap.data();
        setStatus(data.status);
        if (data.status === "approved") {
          setTimeout(() => {
            navigate("/dashboard-doctor");
          }, 3000);
        }
      }
    } catch (error) {
      console.error("Error checking verification:", error);
      setError("Failed to check verification status.");
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = [
      "image/jpeg",
      "image/png",
      "image/jpg",
      "application/pdf",
    ];
    if (!validTypes.includes(file.type)) {
      setError("Please upload only JPG, PNG, or PDF files.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("File size must be less than 5MB.");
      return;
    }

    setFiles({ license: file });

    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (ev) => setPreviews({ license: ev.target.result });
      reader.readAsDataURL(file);
    } else {
      setPreviews({ license: null });
    }

    setError("");
  };

  const removeFile = () => {
    setFiles({ license: null });
    setPreviews({ license: null });
  };

  const viewFile = () => {
    if (previews.license) window.open(previews.license, "_blank");
  };

  const handleUpload = async () => {
    if (!files.license) {
      setError("Please upload your medical license.");
      return;
    }

    setUploading(true);
    setError("");
    setSuccess("");

    try {
      const storageRef = (name) =>
        ref(storage, `doctor_documents/${user.uid}/${name}`);

      const uploadFile = async (file, name) => {
        await uploadBytes(storageRef(name), file);
        return await getDownloadURL(storageRef(name));
      };

      const licenseURL = await uploadFile(files.license, "license");

      await setDoc(doc(db, "verifications", user.uid), {
        licenseURL,
        status: "pending",
        submittedAt: new Date(),
        doctorId: user.uid,
        doctorEmail: user.email,
      });

      setSuccess(
        "License uploaded successfully! Your verification is now pending review."
      );
      setStatus("pending");
    } catch (error) {
      console.error("Upload error:", error);
      setError("Failed to upload document. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const allFilesUploaded = !!files.license;

  if (loading) {
    return (
      <div style={styles.container}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100vh",
          }}
        >
          <div style={styles.spinner}></div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .upload-button:hover {
          background: linear-gradient(135deg, #1d4ed8, #4338ca) !important;
          transform: translateY(-1px);
        }

        .submit-button:hover:not(:disabled) {
          background: linear-gradient(135deg, #1d4ed8, #4338ca) !important;
          transform: translateY(-2px);
          box-shadow: 0 20px 25px -5px rgba(59, 130, 246, 0.4) !important;
        }

        .document-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
        }

        .preview-button:hover {
          transform: scale(1.1);
        }

        @media (max-width: 768px) {
          .documents-grid { grid-template-columns: 1fr !important; }
          .form-section { padding: 24px !important; }
          .header-content { padding: 0 16px !important; }
        }
      `}</style>

      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerContent} className="header-content">
          <div style={styles.logoContainer}>
            <Activity style={{ color: "white", width: 24, height: 24 }} />
          </div>
          <div>
            <h1 style={styles.headerTitle}>Doctor Verification</h1>
            <p style={styles.headerSubtitle}>
              Verify your medical credentials
            </p>
          </div>
        </div>
      </div>

      <div style={styles.mainContent}>
        <div style={styles.card}>
          {/* Status Banner */}
          {status && (
            <div
              style={{
                ...styles.statusBanner,
                ...(status === "pending"
                  ? styles.statusPending
                  : status === "approved"
                  ? styles.statusApproved
                  : styles.statusRejected),
              }}
            >
              <div style={styles.statusIcon}>
                {status === "pending" && (
                  <Clock style={{ width: 48, height: 48 }} />
                )}
                {status === "approved" && (
                  <CheckCircle style={{ width: 48, height: 48 }} />
                )}
                {status === "rejected" && (
                  <AlertCircle style={{ width: 48, height: 48 }} />
                )}
              </div>

              <h2 style={styles.statusTitle}>
                {status === "pending" && "Verification Pending"}
                {status === "approved" && "Verification Approved"}
                {status === "rejected" && "Verification Rejected"}
              </h2>

              <p style={styles.statusDescription}>
                {status === "pending" &&
                  "Your document is being reviewed by our team. This typically takes 1–2 business days."}
                {status === "approved" &&
                  "Congratulations! Your medical credentials have been verified. Redirecting to dashboard..."}
                {status === "rejected" &&
                  "Your verification was rejected. Please contact support for more information."}
              </p>
            </div>
          )}

          {/* Form Section */}
          {(!status || status === "rejected") && (
            <div style={styles.formSection} className="form-section">
              <h2 style={styles.sectionTitle}>
                <Shield style={{ width: 24, height: 24, color: "#3b82f6" }} />
                License Verification
              </h2>

              <p style={styles.sectionDescription}>
                To ensure safety and trust on the platform, we need to verify
                your medical license. Please upload a clear photo or a PDF of
                your valid license.
              </p>

              {/* Requirements */}
              <div style={styles.requirements}>
                <h3
                  style={{
                    fontSize: "16px",
                    fontWeight: "600",
                    color: "#111827",
                    marginBottom: "16px",
                  }}
                >
                  Document Requirements
                </h3>
                <ul style={styles.requirementsList}>
                  <li style={styles.requirementItem}>
                    <CheckCircle style={styles.requirementIcon} />
                    Clear, readable image or PDF
                  </li>
                  <li style={styles.requirementItem}>
                    <CheckCircle style={styles.requirementIcon} />
                    File size under 5MB
                  </li>
                  <li style={styles.requirementItem}>
                    <CheckCircle style={styles.requirementIcon} />
                    License must be current and valid
                  </li>
                  <li style={styles.requirementItem}>
                    <CheckCircle style={styles.requirementIcon} />
                    All text and seals must be legible
                  </li>
                </ul>
              </div>

              {/* Alerts */}
              {error && (
                <div style={{ ...styles.alert, ...styles.errorAlert }}>
                  <AlertCircle style={{ width: 16, height: 16 }} />
                  {error}
                </div>
              )}
              {success && (
                <div style={{ ...styles.alert, ...styles.successAlert }}>
                  <CheckCircle style={{ width: 16, height: 16 }} />
                  {success}
                </div>
              )}

              {/* Single Document Upload Card */}
              <div
                style={styles.documentsGrid}
                className="documents-grid"
              >
                <div
                  style={{
                    ...styles.documentCard,
                    ...(files.license ? styles.documentCardUploaded : {}),
                  }}
                  className="document-card"
                  onClick={() =>
                    !files.license && document.getElementById("file-license").click()
                  }
                >
                  {files.license && (
                    <div style={styles.filePreview}>
                      {previews.license && (
                        <button
                          style={{ ...styles.previewButton, ...styles.viewButton }}
                          className="preview-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            viewFile();
                          }}
                          title="View file"
                        >
                          <Eye style={{ width: 16, height: 16 }} />
                        </button>
                      )}
                      <button
                        style={{ ...styles.previewButton, ...styles.removeButton }}
                        className="preview-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile();
                        }}
                        title="Remove file"
                      >
                        <X style={{ width: 16, height: 16 }} />
                      </button>
                    </div>
                  )}

                  <div style={styles.documentIcon}>
                    <Shield style={{ width: 24, height: 24, color: "#10b981" }} />
                  </div>

                  <h3 style={styles.documentTitle}>{documentType.title}</h3>
                  <p style={styles.documentDescription}>
                    {documentType.description}
                  </p>

                  {files.license ? (
                    <div style={styles.fileName}>
                      <FileText
                        style={{
                          width: 16,
                          height: 16,
                          display: "inline",
                          marginRight: 8,
                        }}
                      />
                      {files.license.name}
                    </div>
                  ) : (
                    <button style={styles.uploadButton} className="upload-button">
                      <Upload style={{ width: 16, height: 16 }} />
                      Upload File
                    </button>
                  )}

                  <input
                    id="file-license"
                    type="file"
                    accept="image/*,.pdf"
                    style={styles.fileInput}
                    onChange={handleFileChange}
                  />
                </div>
              </div>

              {/* Submit */}
              <button
                style={{
                  ...styles.submitButton,
                  ...(uploading || !allFilesUploaded
                    ? styles.submitButtonDisabled
                    : {}),
                }}
                className="submit-button"
                onClick={handleUpload}
                disabled={uploading || !allFilesUploaded}
              >
                {uploading ? (
                  <>
                    <div style={styles.spinner}></div>
                    Uploading License...
                  </>
                ) : (
                  <>
                    <Shield style={{ width: 20, height: 20 }} />
                    Submit for Verification
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyDoctor;
